import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import { green, red } from '@mui/material/colors'
import React, { useState } from 'react'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { InvoiceManager } from '../../services/api/InvoiceManager'
import {
    FioCallOutcome,
    ForcePaymentCheckResultDto,
} from '../../services/api/collections/invoice/forcePaymentCheck/forcePaymentCheck_200'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'

interface ForcePaymentCheckDialogProps {
    open: boolean
    onClose: () => void
    onSuccess?: () => void
}

type DialogPhase = 'idle' | 'loading' | 'done'

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('cs-CZ')
const formatAmount = (amount: number) => `${amount.toLocaleString('cs-CZ')} Kč`

const ForcePaymentCheckDialog: React.FC<ForcePaymentCheckDialogProps> = ({ open, onClose, onSuccess }) => {
    const dispatch = useAppDispatch()
    const [phase, setPhase] = useState<DialogPhase>('idle')
    const [response, setResponse] = useState<ForcePaymentCheckResultDto | null>(null)

    const reset = () => {
        setPhase('idle')
        setResponse(null)
    }

    const handleClose = () => {
        if (phase === 'loading') return
        const shouldRefetch = phase === 'done' && response !== null && !response.alreadyRunning
        reset()
        onClose()
        if (shouldRefetch) onSuccess?.()
    }

    const handleRun = async () => {
        setPhase('loading')
        try {
            const result = await InvoiceManager.getManager().forcePaymentCheckAsync()
            setResponse(result)
            setPhase('done')
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setPhase('idle')
        }
    }

    const renderTokenResult = (outcome: FioCallOutcome, matchedCount: number, httpStatusCode: number | null) => {
        if (outcome === FioCallOutcome.Success) {
            if (matchedCount > 0) {
                return { color: green[800], text: `spárováno ${matchedCount}` }
            }
            return { color: 'text.secondary', text: 'žádné nové platby' }
        }
        if (outcome === FioCallOutcome.HttpError && httpStatusCode === 409) {
            return { color: red[800], text: 'FIO povolí dotaz na stejný účet jen jednou za 30 s – počkejte chvíli a zkuste znovu' }
        }
        if (outcome === FioCallOutcome.HttpError && httpStatusCode === 422) {
            return { color: red[800], text: 'FIO nemá odemčenou rozšířenou historii – v internetbankingu odemkněte přístup (Nastavení → API → ikonka zámku) a zkuste to znovu do 10 minut' }
        }
        if (outcome === FioCallOutcome.HttpError && (httpStatusCode === 401 || httpStatusCode === 403)) {
            return { color: red[800], text: 'FIO odmítla token – zkontrolujte, zda je v databázi platný token této banky' }
        }
        if (outcome === FioCallOutcome.HttpError) {
            return { color: red[800], text: `HTTP chyba ${httpStatusCode ?? ''}` }
        }
        if (outcome === FioCallOutcome.NetworkError) {
            return { color: red[800], text: 'chyba sítě' }
        }
        return { color: red[800], text: 'chyba zpracování odpovědi' }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
            <DialogTitle>Zkontrolovat platby (starší než 90 dní)</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {phase === 'idle' && (
                    <Stack spacing={2}>
                        <Alert severity='info' variant='filled'>
                            Tato akce projde všechny nezaplacené faktury a zkusí je spárovat s bankovními transakcemi až od
                            data vystavení nejstarší nezaplacené faktury. Kvůli bezpečnostnímu zámku na FIO API je nutné
                            nejprve v internetbankingu povolit rozšířenou historii.
                        </Alert>
                        <Typography variant='subtitle1' fontWeight='bold'>
                            Postup
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText primary='1. Přihlaste se do internetového bankovnictví FIO.' />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary='2. Otevřete Nastavení → API.' />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary='3. U příslušného tokenu klikněte na ikonu zámku a autorizujte (SMS/push).' />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary='4. Kompletní historie bude dostupná pouze 10 minut — v tomto okně proveďte kontrolu.' />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary='5. Vraťte se sem a klikněte "Povoleno, zkontroluj".' />
                            </ListItem>
                        </List>
                        <Typography variant='caption'>
                            Dokumentace:{' '}
                            <Link href='https://www.fio.cz/docs/cz/API_Bankovnictvi.pdf' target='_blank' rel='noopener'>
                                FIO API (PDF, sekce 3.1)
                            </Link>
                        </Typography>
                    </Stack>
                )}
                {phase === 'loading' && (
                    <Stack spacing={2} alignItems='center' py={4}>
                        <CircularProgress />
                        <Typography>Stahuji transakce z FIO…</Typography>
                    </Stack>
                )}
                {phase === 'done' && response && (
                    <Stack spacing={2}>
                        {response.alreadyRunning ? (
                            <Alert severity='warning'>
                                Kontrola již běží. Zkuste to prosím za chvíli.
                            </Alert>
                        ) : (
                            <>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    Výsledek kontroly
                                </Typography>
                                <Stack spacing={1}>
                                    {response.tokenResults.map((tr, idx) => {
                                        const status = renderTokenResult(tr.outcome, tr.matchedCount, tr.httpStatusCode)
                                        return (
                                            <Paper
                                                key={`${tr.safeTokenId}-${idx}`}
                                                variant='outlined'
                                                sx={{ p: 2, borderLeft: 4, borderLeftColor: status.color }}
                                            >
                                                <Typography fontWeight='medium'>{tr.issuerName}</Typography>
                                                <Typography variant='body2' sx={{ color: status.color }}>
                                                    {status.text}
                                                </Typography>
                                                {tr.errorMessage && (
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {tr.errorMessage}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        )
                                    })}
                                </Stack>
                                {response.matchedInvoices.length > 0 && (
                                    <>
                                        <Typography variant='subtitle1' fontWeight='bold'>
                                            Spárované faktury
                                        </Typography>
                                        <Stack spacing={1}>
                                            {response.matchedInvoices.map((m) => (
                                                <Paper key={m.invoiceId} variant='outlined' sx={{ p: 2 }}>
                                                    <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                                                        <Typography fontWeight='medium'>Faktura {m.invoiceNumber}</Typography>
                                                        <Typography fontWeight='medium' sx={{ color: green[800] }}>
                                                            {formatAmount(m.amount)}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant='body2' color='text.secondary'>
                                                        {m.clientName ?? '—'} · {m.eventName ?? '—'} · zaplaceno {formatDate(m.paidAt)}
                                                    </Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {phase === 'idle' && (
                    <>
                        <Button onClick={handleClose}>Zrušit</Button>
                        <Button onClick={handleRun} variant='contained'>
                            Povoleno, zkontroluj
                        </Button>
                    </>
                )}
                {phase === 'loading' && (
                    <Button disabled>Zrušit</Button>
                )}
                {phase === 'done' && (
                    <Button onClick={handleClose} variant='contained'>
                        Zavřít
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default ForcePaymentCheckDialog
